import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Heading,
  Text,
  Container,
  SimpleGrid,
  Button,
  VStack,
  Icon,
  Flex,
} from "@chakra-ui/react";
import Card from "./Card";
import { projects as allProjects } from "../pages/projects/projectData";
import { FaArrowRight } from "react-icons/fa";
import ScrollAnimationWrapper from "./ScrollAnimationWrapper";

// Select featured projects and create shortened descriptions
const featuredProjects = [
  {
    ...allProjects.find(p => p.id === "tempa-digital"),
    description: "A boutique design and development studio crafting digital products, pipelines, and platforms — built with Next.js 16, React Three Fiber, and motion design."
  },
  {
    ...allProjects.find(p => p.id === "midpoint-maps"),
    description: "Sophisticated web app revolutionizing group meetups through collaborative workflows, real-time optimization, and comprehensive place discovery."
  },
  {
    ...allProjects.find(p => p.id === "gunung"),
    description: "Official distribution platform with international climbing brand partnerships, B2B distribution network, and D2C e-commerce store."
  },
  {
    ...allProjects.find(p => p.id === "korean-learning"),
    description: "Modern Korean language learning platform with secure Stripe payments, student management, and optimized media delivery."
  },

];

const ProjectsSection = () => {
  const navigate = useNavigate();

  // Define the click handler function
  const handleCardClick = (projectId) => {
    navigate(`/projects/${projectId}`);
  };

  const handleViewAllClick = () => {
    navigate('/projects');
  };

  return (
    <Box
      as="section"
      id="projects-section"
      bg="brand.primary"
      py={{ base: "4rem", md: "5rem" }}
      position="relative"
      overflow="hidden"
    >
      {/* Background decorative elements */}
      <Box
        position="absolute"
        top="15%"
        right="-5%"
        w="300px"
        h="300px"
        borderRadius="full"
        bg="rgba(49, 130, 206, 0.07)"
        filter="blur(70px)"
        zIndex={0}
      />
      <Box
        position="absolute"
        bottom="-10%"
        left="-5%"
        w="250px"
        h="250px"
        borderRadius="full"
        bg="rgba(246, 173, 85, 0.05)"
        filter="blur(100px)"
        zIndex={0}
      />

      <Container maxW="1200px" position="relative" zIndex={1}>
        <VStack spacing={{ base: 8, md: 12 }} align="stretch">
          {/* Section header */}
          <ScrollAnimationWrapper>
            <VStack spacing={3} align="center" textAlign="center">
              <Text
                color="brand.accent"
                fontWeight="600"
                letterSpacing="1px"
                textTransform="uppercase"
                fontSize="sm"
              >
                Case Studies
              </Text>
              
              <Heading
                as="h2"
                fontSize={{ base: "3xl", md: "4xl" }}
                fontWeight="bold"
                textAlign="center"
                lineHeight="1.2"
              >
                Featured Projects
              </Heading>
              
              <Text
                color="brand.text.secondary"
                maxW="600px"
                fontSize={{ base: "md", md: "lg" }}
              >
                Explore a selection of my recent work across different domains
              </Text>
            </VStack>
          </ScrollAnimationWrapper>

          {/* Projects grid */}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8} w="100%">
            {featuredProjects.map((project, index) => (
              <ScrollAnimationWrapper
                key={project.id}
                delay={0.1 * index}
                direction="up"
              >
                <Card
                  id={project.id}
                  title={project.title}
                  description={project.description}
                  image={project.image}
                  categories={project.categories}
                  technologies={project.technologies}
                  demoLink={project.demoLink}
                  githubLink={project.githubLink}
                  dissertationLink={project.dissertationLink}
                  onClick={() => handleCardClick(project.id)}
                />
              </ScrollAnimationWrapper>
            ))}
          </SimpleGrid>

          {/* View all projects button */}
          <ScrollAnimationWrapper delay={0.4} direction="up">
            <Flex
              justify="center"
              mt={{ base: 6, md: 10 }}
            >
              <Button
                onClick={handleViewAllClick}
                rightIcon={<Icon as={FaArrowRight} />}
                colorScheme="blue"
                size="lg"
                px={8}
                fontSize="md"
                _hover={{
                  transform: "translateY(-2px)",
                  boxShadow: "lg",
                }}
              >
                View All Projects
              </Button>
            </Flex>
          </ScrollAnimationWrapper>
        </VStack>
      </Container>
    </Box>
  );
};

export default ProjectsSection;
